import { FaixaAssinatura } from '@/components/FaixaAssinatura';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import './globals.css';
import { Button } from '@/components/ui/button';
import { Link, Outlet, RouteObject, useLocation, useMatch, useMatches, useNavigate, useParams } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useWindowSize } from '@/hooks/window-size';
import { Sidebar, SidebarMobile } from '@/components/sidebar/sidebar';
import { menus as todosMenus } from '@/menus';
import { temPermissao } from '@/utils/permissao';
import { Building2, ChevronDown, KeyRound, LogOut, Moon, Sun, UserRound } from 'lucide-react';
import { useTheme } from '@/components/theme-provider';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import axios from 'axios';
import { keycloak } from './keycloak';
import useAuth from './components/hooks/use.auth';
import { Toaster, toast } from 'sonner';
import { mensagemDeErro } from './utils/erro-api';
import { routes } from './routes';
import { getTenant } from './utils/TenantUtils';
import { useMarca } from './components/providers/MarcaProvider';

// Token do Keycloak (renovado quando falta menos de 30s) e tenant em todas as chamadas da API
axios.interceptors.request.use(async config => {
  if (config.url!.startsWith("/api")) {
    await keycloak.updateToken(30).catch(() => keycloak.login());
    config.headers['Authorization'] = `Bearer ${keycloak.token}`;
    config.headers['x-tenant'] = getTenant();
  }
  return config;
});

// Erros ao salvar ou excluir aparecem sempre como aviso; consultas (GET) são tratadas por cada tela
axios.interceptors.response.use(
  response => response,
  error => {
    const url: string = error.config?.url || '';
    if (error.config?.method !== 'get' && url.startsWith('/api')) {
      toast.error(mensagemDeErro(error));
    }
    return Promise.reject(error);
  }
);

const iniciais = (nome?: string) =>
  (nome || '').split(' ').filter(Boolean).map(p => p[0]).filter((_, i, a) => i === 0 || i === a.length - 1).join('').toUpperCase();

function App() {
  const [menuOpened, setMenuOpened] = useState<boolean>(false);
  const [menuManual, setMenuManual] = useState<boolean>(false);
  const {setTheme, isThemeDark} = useTheme();
  const {width} = useWindowSize();
  const matches = useMatches();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const {tenant} = useParams();
  const marca = useMarca();
  const [isMobile, setIsMobile] = useState(false)
  // Isentas (situação nula ou ISENTA) não usam assinatura pelo site
  const isenta = !marca.situacao || marca.situacao === 'ISENTA';
  const menus = todosMenus.filter(menu => temPermissao(auth.user, tenant, menu.permissao) && !(isenta && menu.href === '/assinatura'));

  useEffect(() => {
    if (!menuManual || width! <= 640){
      setMenuOpened(width! >= 1280);
      setMenuManual(false);
    }
    setIsMobile(width! <= 640);
  },[width, menuManual]);

  const getItens = () => {
    var itens: any[] = [];
    matches.map(item => {
      if (item.pathname === '/'){
        itens.push(item);
      } else {
        let originalPathName = item.pathname;
        for (var key in item.params) {
          originalPathName = originalPathName.replaceAll(item.params[key]!, `:${key}`);
        }
        var found = filterRouteByPath(originalPathName);
        // A rota do dashboard (/:tenant) não tem título próprio: é a "Página inicial"
        if (found && found.handle){
          if (found.parent){
            let subItens = getSubItens([], found.parent);
            subItens.reverse().forEach(subItem => itens.push(subItem));
          }
          found.last = false;
          itens.push(found);
        }
      }
    })
    itens[itens.length-1].last = true;
    return itens;
  }

  const getSubItens = (itens: any[], parent: string) => {
    let parentRoute = filterRouteByPath(parent);
    if (parentRoute){
      parentRoute.last = false;
      itens.push(parentRoute);
      if (parentRoute.parent){
        getSubItens(itens, parentRoute.parent);
      }
    }
    return itens;
  }

  const filterRouteByPath = (path: string) => {
    return routes[0].children?.filter((r: any) => r.path === path).shift();
  }

  const defaultUrlBuilder = (path: string) => {
    return `${location.pathname.match(/\/[0-9a-f]{8}/)![0]}${path.replace('/:tenant','')}`
  }

  const breadcrumb = (): ReactNode => {
    return (
      <Breadcrumb className="hidden sm:flex">
        <BreadcrumbList>
          {
            getItens().map((item, index) => (
              <Fragment key={index}>
              {!item.last ? (
                <>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={(item.urlBuilder && item.urlBuilder(location.pathname)) || 
                    (item.path && defaultUrlBuilder(item.path)) || (item.pathname && defaultUrlBuilder(item.pathname))}><>{item.handle ? item.handle : '?'}</></Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                </>
              ):(
                <BreadcrumbItem>
                  <BreadcrumbPage><>{location.pathname.includes("/novo") ? "Novo" : item.handle ? item.handle : '?'}</></BreadcrumbPage>
                </BreadcrumbItem>
              )}
              </Fragment>
            ))
          }
        </BreadcrumbList>
      </Breadcrumb>
    )
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-muted/40 pb-20">
      <Toaster position="top-right" richColors />
      <Sidebar menus={menus} opened={menuOpened} tenant={tenant} onToggle={() => {
          setMenuManual(true);
          setMenuOpened(!menuOpened);
        }} />
      <div className={`flex flex-col sm:gap-4 ${menuOpened ? 'pl-64' : 'sm:pl-14'}`}>
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur sm:px-6">
          {isMobile && (
            <SidebarMobile menus={menus} tenant={tenant} opened={menuOpened} 
              closeMenu={() => setMenuOpened(false)}
              openMenu={() => setMenuOpened(true)} />
          )}
          {marca.empresa && (
            <span className="flex min-w-0 max-w-[40%] shrink-0 items-center gap-2 border-r pr-3 text-sm font-semibold sm:max-w-xs">
              <Building2 aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="truncate" title={marca.empresa}>{marca.empresa}</span>
            </span>
          )}
          <div className="min-w-0 flex-1">{breadcrumb()}</div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="flex items-center gap-2.5 rounded-md p-1.5 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:pr-2">
                <span aria-hidden className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {iniciais(auth.user?.nome)}
                </span>
                <span className="hidden max-w-[12rem] truncate text-sm font-medium md:block">{auth.user?.nome}</span>
                <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:block" />
                <span className="sr-only">Abrir menu do usuário</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="font-normal">
                <p className="truncate text-sm font-medium">{auth.user?.nome}</p>
                <p className="truncate text-xs text-muted-foreground">{auth.user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* Ler o próprio cadastro usa a API de usuários (USUARIO_READ) */}
              {temPermissao(auth.user, tenant, 'USUARIO_READ') && (
                <DropdownMenuItem className='cursor-pointer gap-2' onClick={() => navigate(`/${tenant}/usuario/${auth.user?.id}`)}>
                  <UserRound className="h-4 w-4 text-muted-foreground" /> Minha conta
                </DropdownMenuItem>
              )}
              {temPermissao(auth.user, tenant, 'USUARIO_CHANGE_PASSWORD') && (
                <DropdownMenuItem className='cursor-pointer gap-2' onClick={auth.alterarSenha}>
                  <KeyRound className="h-4 w-4 text-muted-foreground" /> Alterar senha
                </DropdownMenuItem>
              )}
              <DropdownMenuItem className='cursor-pointer gap-2' onClick={() => isThemeDark() ? setTheme('light') : setTheme('dark')}>
                {isThemeDark() ? <Sun className="h-4 w-4 text-muted-foreground" /> : <Moon className="h-4 w-4 text-muted-foreground" />}
                Modo {isThemeDark() ? 'claro' : 'escuro'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className='cursor-pointer gap-2' onClick={auth.signout}>
                <LogOut className="h-4 w-4 text-muted-foreground" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <FaixaAssinatura />
        <main className="grid flex-1 grid-cols-1 items-start gap-4 p-4 sm:px-6 sm:pb-0 sm:pt-2 md:gap-8">
          <div className="grid grid-cols-1 auto-rows-max items-start gap-4 lg:col-span-2">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export default App;
